import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import classNames from 'classnames';
import { getAbundanceForValue, getValueForAbundance } from 'constants/fcm';
import './AbundanceSlider.scss';
import useResizeObserver from '@react-hook/resize-observer';
import icons from 'img/icons';

const AbundanceSlider = ({
  changeable,
  node,
  onChange,
  value,
  label,
  isStatic,
}) => {
  const handleRef = useRef(null);
  const svgRef = useRef(null);
  const [abundance, setAbundance] = useState(value);
  const [width, setWidth] = useState(0);

  useEffect(() => setAbundance(value), [value, setAbundance]);

  useEffect(() => {
    setWidth(svgRef.current.parentElement.getBoundingClientRect().width);
  }, [svgRef]);

  useResizeObserver(svgRef?.current?.parentElement, () =>
    setWidth(svgRef?.current.parentElement?.getBoundingClientRect()?.width)
  );

  const rectHeight = 4;
  const rectVerticalMargin = 6;
  const rectHorizontalMargin = 15;
  // Space reserved above the track for top-row labels (two lines of 0.75rem text + gap)
  const topPadding = 28;
  const trackAbsY = rectVerticalMargin + topPadding; // 34
  const bottomGroupAbsY = trackAbsY + rectHeight + 15; // 53
  const topGroupAbsY = topPadding - 18; // 10 — first-tspan baseline
  const svgHeight = bottomGroupAbsY + 30; // 83
  const rectWidth = Math.max(0, width - 2 * rectHorizontalMargin);

  const xScale = d3
    .scaleLinear()
    .domain([0, 1])
    .range([rectHorizontalMargin, rectHorizontalMargin + rectWidth])
    .clamp(true);

  useEffect(() => {
    const getUpdatedPosition = (x, width) => {
      if (x < rectHorizontalMargin) {
        return rectHorizontalMargin;
      }
      if (x > width + rectHorizontalMargin) {
        return width + rectHorizontalMargin;
      }
      return x;
    };

    if (changeable) {
      d3.select(handleRef.current).call(
        d3
          .drag()
          .on('drag', d => {
            const x = getUpdatedPosition(d3.event.x, rectWidth);
            setAbundance(xScale.invert(x));
          })
          .on('end', () => {
            const x = getUpdatedPosition(d3.event.x, rectWidth);
            onChange(xScale.invert(x).toFixed(3));
          })
      );
    }
  }, [changeable, handleRef, node, onChange, xScale, rectWidth]);

  const population = getValueForAbundance(node, abundance);
  const grabbableAreaWidth = 20;

  let heightRef = useRef(null);

  return (
    <div className={classNames('AbundanceSlider', { static: isStatic })}>
      {label ? (
        <div className={`text-interface-subtext  InputField-label`}>
          {label}
        </div>
      ) : null}

      <div className="AbundanceSlider-slider-container">
        <svg ref={svgRef} width={'100%'} height={svgHeight}>
          <g ref={heightRef}>
            <rect
              x={rectHorizontalMargin}
              y={trackAbsY}
              width={rectWidth}
              height={rectHeight}
              className="current-population-line"
              rx={rectHeight / 2}
            />

            <g className="ticks">
              {(() => {
                const charWidth = 6.5;
                const minGap = 4;

                const tickData = Object.entries(node.membershipFunctions)
                  .map(([funcName, func]) => {
                    const population = func[1][0];
                    const x = xScale(getAbundanceForValue(node, population));
                    const numberText =
                      +population === Math.round(population)
                        ? String(population)
                        : (+population).toFixed(1);
                    const labelText =
                      funcName[0] + funcName.slice(1).toLocaleLowerCase();
                    const estimatedWidth =
                      Math.max(numberText.length, labelText.length) *
                        charWidth +
                      4;
                    return {
                      funcName,
                      originalX: x,
                      adjustedX: x,
                      numberText,
                      labelText,
                      estimatedWidth,
                    };
                  })
                  .sort((a, b) => {
                    const order = [
                      'VERY LOW',
                      'LOW',
                      'MOD',
                      'HIGH',
                      'VERY HIGH',
                    ];
                    const ai = order.indexOf(a.funcName);
                    const bi = order.indexOf(b.funcName);
                    if (ai !== -1 && bi !== -1) return ai - bi;
                    if (ai !== -1) return -1;
                    if (bi !== -1) return 1;
                    return a.originalX - b.originalX;
                  });

                // Assign rows: even index = bottom, odd = top
                // This guarantees first and last are always bottom
                tickData.forEach((t, i) => {
                  t.row = i % 2 === 0 ? 'bottom' : 'top';
                });

                // Collision avoidance within each row independently
                ['bottom', 'top'].forEach(row => {
                  const rowTicks = tickData.filter(t => t.row === row);
                  // Forward pass
                  for (let i = 1; i < rowTicks.length; i++) {
                    const prev = rowTicks[i - 1];
                    const curr = rowTicks[i];
                    const minX =
                      prev.adjustedX +
                      prev.estimatedWidth / 2 +
                      curr.estimatedWidth / 2 +
                      minGap;
                    if (curr.adjustedX < minX) curr.adjustedX = minX;
                  }
                  // Backward pass
                  for (let i = rowTicks.length - 2; i >= 0; i--) {
                    const next = rowTicks[i + 1];
                    const curr = rowTicks[i];
                    const maxX =
                      next.adjustedX -
                      next.estimatedWidth / 2 -
                      curr.estimatedWidth / 2 -
                      minGap;
                    if (curr.adjustedX > maxX) curr.adjustedX = maxX;
                  }
                });

                return (
                  <>
                    {/* Leader lines rendered first (underneath labels) */}
                    {tickData.map(({ funcName, originalX, adjustedX, row }) => {
                      const isTop = row === 'top';
                      return (
                        <line
                          key={`${funcName}-leader`}
                          className="tick-connector"
                          pointerEvents="none"
                          x1={originalX}
                          y1={isTop ? trackAbsY : trackAbsY + rectHeight}
                          x2={adjustedX}
                          y2={
                            isTop ? trackAbsY - 8 : trackAbsY + rectHeight + 10
                          }
                        />
                      );
                    })}

                    {/* Label groups */}
                    {tickData.map(
                      ({ funcName, adjustedX, numberText, labelText, row }) => {
                        const isTop = row === 'top';
                        const groupY = isTop ? topGroupAbsY : bottomGroupAbsY;

                        return (
                          <g
                            key={funcName}
                            className="tick"
                            transform={`translate(${adjustedX}, ${groupY})`}
                            pointerEvents="none"
                          >
                            {isTop ? (
                              <text className="tick-text" y={0}>
                                <tspan x={0}>{labelText}</tspan>
                                <tspan x={0} dy="1em">
                                  {numberText}
                                </tspan>
                              </text>
                            ) : (
                              <>
                                <line
                                  x1={0}
                                  x2={0}
                                  y1={11}
                                  y2={rectHeight}
                                  stroke="white"
                                />
                                <text className="tick-text" y={rectHeight + 3}>
                                  <tspan x={0}>{numberText}</tspan>
                                  <tspan x={0} dy="1em">
                                    {labelText}
                                  </tspan>
                                </text>
                              </>
                            )}
                          </g>
                        );
                      }
                    )}
                  </>
                );
              })()}
            </g>

            <g
              ref={handleRef}
              className={classNames('currentPopulation', {
                draggable: changeable,
              })}
              transform={`translate(${xScale(abundance)}, 0)`}
            >
              <circle
                className="current-population-line-endcap-middle"
                r={rectVerticalMargin}
                transform={`translate(0, ${trackAbsY + rectHeight / 2})`}
              />
              <circle
                className="current-population-line-endcap-inner"
                r={rectHeight}
                transform={`translate(0, ${trackAbsY + rectHeight / 2})`}
              />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default AbundanceSlider;
